import { useContext, useEffect, useState } from "react";
import {
  haveValue,
  convertCurrencyFormat,
  getUserDisplayName,
  toast,
  shouldDeductNextView,
} from "../../helper/common";
import useTranslator from "../../hooks/useTranslator";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { calculateNowTime } from "../../services/sharedService";
import AuthContext from "../../context/auth/AuthContext";
import LandLordContext from "../../context/landlord/LandLordContext";
import AddPropertyModal from "../../pages/landlord/profile-request/AddPropertyModal";
import ServingPromptModal from "../../pages/landlord/profile-request/ServingPromptModal";
import { WORKSPACE_ROLES_CAPABILITIES } from "../../Utils/workspaceRoleCapabilities";

const AccordionRow = ({
  profile,
  aggregatedData,
  properties,
  loadData,
  totalParticipants,
  isOpen,
  onToggle,
}) => {
  const { _t } = useTranslator();
  const authContext = useContext(AuthContext);
  const { user, setLoading } = authContext;
  const landLordContext = useContext(LandLordContext);
  const { propertiesAll, editSharedProfile } = landLordContext;
  const location = useLocation();
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const [linkedPropertiesModel, setLinkedPropertiesModel] = useState(false);
  const [propertyLink, setPropertyLink] = useState([]);
  const [servingPromptModal, setServingPromptModal] = useState(false);

  const isHighlight =
    profile?.opened_at &&
    profile?.sortDate &&
    new Date(profile.sortDate) > new Date(profile.opened_at);

  const applicationMembers =
    profile?.application_id?.participants
      ?.filter((p) => p.consent_status === "accepted" && p.tenant_info)
      ?.map((p) => getUserDisplayName(p.tenant_info))
      ?.filter(Boolean) ?? [];

  const applicationName = profile?.application_id?.title;

  const subtitleMembers = applicationMembers.slice(1, 3).join(", ");
  const hasMoreMembers = applicationMembers.length > 3;

  useEffect(() => {
    if (haveValue(properties) && haveValue(profile?.propertyid)) {
      setPropertyLink(profile?.propertyid);
    }
  }, [properties, profile]);

  const handleLinkProperty = async (
    propertyToAdd = null,
    propertyToRemove = null
  ) => {
    if (haveValue(propertyToAdd)) {
      const data = {
        property_id: propertyToAdd,
        status: "add",
      };
      await editSharedProfile(profile?._id, data)
        .then(async () => {
          await loadData();
        })
        .catch((err) => {
          console.log(err);
        });
    } else if (haveValue(propertyToRemove)) {
      const data = {
        property_id: propertyToRemove,
        status: "remove",
      };
      await editSharedProfile(profile?._id, data)
        .then(async () => {
          await loadData();
        })
        .catch((err) => {
          console.log(err);
        });
    } else {
      toast.error(_t("pleaseSelectPropertyToLink"));
    }
  };

  const handleUnlinkProperty = async (propertyId) => {
    setPropertyLink(propertyLink.filter((val) => val !== propertyId));
    await handleLinkProperty(null, propertyId);
  };

  const handleOpenProfile = async () => {
    try {
      setLoading(true);
      const checkView = await shouldDeductNextView(user, profile?._id);
      if (checkView) {
        setServingPromptModal(true);
      } else {
        navigate(
          `/${workspaceId}/profile-property?sharedprofileid=${profile?._id}`,
          {
            state: { from: location.pathname + location.search },
          }
        );
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <tr
        className={`hideOnMobile second-row relative cursor-pointer ${isHighlight ? "highlightRow" : ""}
        ${isOpen ? "accordion-open" : ""}`}
        onClick={onToggle}
      >
        <td>
          <div
            className="d-flex align-items-center custom-gap-3 profile-accordion-tenant-cell"
            style={{ marginInlineEnd: "0.5vw" }}
          >
            <div className="position-relative list-member-wrapper">
              <div
                className="earner-thumbnail-wrapper d-flex align-items-center justify-content-center"
                style={{
                  position: "relative",
                  padding: "0.2vw",
                }}
              >
                <img
                  src="/images/Flatmates.svg"
                  alt="User Icon"
                  style={{
                    objectFit: "contain",
                  }}
                />
              </div>
              {totalParticipants && (
                <div className="member-count-wrapper d-flex align-items-center justify-content-center">
                  {totalParticipants}
                </div>
              )}
            </div>
            <div className="d-flex flex-column">
              <div className="d-flex align-items-center custom-gap-2">
                <span className="application-title">
                  {/* {aggregatedData.tenantName} */}
                  {applicationName}
                </span>
              </div>
              {subtitleMembers ? (
                <span className="application-subtitle">
                  {_t("with", "with")} {subtitleMembers}
                  {hasMoreMembers ? "..." : ""}
                </span>
              ) : null}
            </div>
            {totalParticipants > 0 && (
              <img
                src="/images/arrow-right.svg"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle();
                }}
                alt="Arrow Down"
                className="cursor-pointer"
                style={{
                  transform: !isOpen ? "rotate(90deg)" : "rotate(270deg)",
                }}
              />
            )}
          </div>
        </td>

        <td className="skor-details-column">{aggregatedData.totalScore}</td>

        <td>€{convertCurrencyFormat(aggregatedData.totalIncome)}</td>

        <td>€{convertCurrencyFormat(aggregatedData.totalEffortRate)}</td>
        {/* 
        <td>
          <div className="d-flex align-items-center">
            <div className="d-flex align-items-center bg-[#DBF1CA] bg_test removeGreenBg">
              <p className="linked_p">
                {profile?.application_id?.participants?.length ??
                  aggregatedData.totalFlatmates}
              </p>
            </div>
          </div>
        </td> */}

        <td>
          <div className="d-flex align-items-center">
            <div className="d-flex align-items-center bg-[#DBF1CA] bg_test removeGreenBg">
              <p className="linked_p">{aggregatedData.totalGuarantors}</p>
            </div>
          </div>
        </td>

        <td>
          <div className="d-flex align-items-center">
            <div
              className={`d-flex align-items-center bg-[#DBF1CA] bg_test removeGreenBg cursor-pointer ${
                profile?.propertyid?.length === 0
                  ? "btngreen"
                  : "link_property_bg"
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setLinkedPropertiesModel(true);
              }}
            >
              <div className="d-flex align-items-center custom-gap-2">
                <p className="linked_p">
                  {profile?.propertyid?.filter((o) =>
                    new Set(propertiesAll?.map((x) => x._id?.toString()))?.has(
                      o?.toString()
                    )
                  ).length || 0}
                </p>
                <img
                  className=""
                  src="/images/arrow-right-green.svg"
                  alt="Open properties"
                />
                {WORKSPACE_ROLES_CAPABILITIES?.properties?.linkTenant?.includes(
                  user?.current_workspace_role
                ) && (
                  <div className="cursor-pointer editButton">
                    <img src="/images/addGreen.svg" alt="Add property" />
                    <p>{_t("add")}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </td>

        <td>
          <div className="d-flex custom-gap-2 align-items-center">
            <div className="d-flex align-items-center custom-gap-2">
              {calculateNowTime(
                aggregatedData.latestUpdateDate,
                localStorage.getItem("i18nextLng")
              )}
            </div>
            {isHighlight && <p className="recentText">{_t("recent")}</p>}
          </div>
        </td>

        <td
          onClick={(e) => {
            e.stopPropagation();
            handleOpenProfile();
          }}
        >
          <div className="cursor-pointer">
            <img
              className="arrowRightTable"
              src="/images/arrow-right-green.svg"
              alt="Open profile"
            />
          </div>
        </td>
      </tr>

      <div className={`hideOnDesktop ${isHighlight ? "highlightRow" : ""}`}>
        <div className="mobile-profile-info-box d-flex align-items-center mobile-profile-gap">
          <div
            className="earner-thumbnail-wrapper d-flex align-items-center justify-content-center"
            style={{
              position: "relative",
              padding: "8px",
            }}
          >
            <img
              src="/images/Flatmates.svg"
              alt="User Icon"
              style={{
                objectFit: "contain",
                width: "32px",
                height: "32px",
              }}
            />
          </div>
          {totalParticipants && (
            <div className="member-count-wrapper d-flex align-items-center justify-content-center">
              {totalParticipants}
            </div>
          )}
          <div className="d-flex flex-column mobile-profile-info-box">
            <div className="d-flex align-items-center custom-gap-2">
              <span className="application-title">
                {aggregatedData.tenantName}
              </span>
            </div>
            {subtitleMembers ? (
              <span className="application-subtitle">
                {_t("with", "with")} {subtitleMembers}
                {hasMoreMembers ? "..." : ""}
              </span>
            ) : null}
          </div>
        </div>

        <div className="mobile-profile-details">
          <div className="mobile-profile-detail-item">
            <span className="detail-label">{_t("skor")}</span>
            <span className="detail-value">{aggregatedData.totalScore}</span>
          </div>

          <div className="mobile-profile-detail-item">
            <span className="detail-label">{_t("income")}</span>
            <span className="detail-value">
              €{convertCurrencyFormat(aggregatedData.totalIncome)}
            </span>
          </div>

          <div className="mobile-profile-detail-item">
            <span className="detail-label">{_t("maxRent")}</span>
            <span className="detail-value">
              €{convertCurrencyFormat(aggregatedData.totalEffortRate)}
            </span>
          </div>

          <div className="mobile-profile-detail-item">
            <span className="detail-label">{_t("members")}</span>
            <span className="detail-value">
              <div className="d-flex align-items-center bg-[#DBF1CA] bg_test removeGreenBg">
                <p className="linked_p">
                  {profile?.application_id?.participants?.length ??
                    aggregatedData.totalFlatmates}
                </p>
              </div>
            </span>
          </div>

          <div className="mobile-profile-detail-item">
            <span className="detail-label">{_t("guarantors")}</span>
            <span className="detail-value">
              <div className="d-flex align-items-center bg-[#DBF1CA] bg_test removeGreenBg">
                <p className="linked_p">{aggregatedData.totalGuarantors}</p>
              </div>
            </span>
          </div>

          <div className="mobile-profile-detail-item">
            <span className="detail-label">{_t("linkedProperties")}</span>
            <span className="detail-value">
              <div
                className={`d-flex align-items-center bg-[#DBF1CA] bg_test removeGreenBg ${
                  profile?.propertyid?.length === 0
                    ? "btngreen"
                    : "link_property_bg"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  setLinkedPropertiesModel(true);
                }}
              >
                <div className="d-flex align-items-center custom-gap-2">
                  <p className="linked_p">
                    {profile?.propertyid?.filter((o) =>
                      new Set(
                        propertiesAll?.map((x) => x._id?.toString())
                      )?.has(o?.toString())
                    ).length || 0}
                  </p>
                  <img
                    className=""
                    src="/images/arrow-right-green.svg"
                    alt="Open properties"
                    style={{ width: "14px", height: "14px" }}
                  />
                </div>
              </div>
            </span>
          </div>
        </div>

        <div className="mobile-profile-footer">
          <div className="d-flex custom-gap-2 align-items-center">
            <div className="d-flex align-items-center custom-gap-2">
              {calculateNowTime(
                aggregatedData.latestUpdateDate,
                localStorage.getItem("i18nextLng")
              )}
            </div>
            {isHighlight && <p className="recentText">{_t("recent")}</p>}
          </div>
          <div
            className="cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenProfile();
            }}
          >
            <img
              className="arrowRightTable"
              src="/images/arrow-right-green.svg"
              alt="Open profile"
              style={{ width: "16px", height: "16px" }}
            />
          </div>
        </div>
      </div>

      <AddPropertyModal
        isOpen={linkedPropertiesModel}
        onClose={() => setLinkedPropertiesModel(false)}
        properties={properties}
        profile={profile}
        handleUnlinkProperty={(id) => handleUnlinkProperty(id)}
        handleLinkProperty={handleLinkProperty}
        setPropertyLink={setPropertyLink}
      />
      <ServingPromptModal
        openModal={servingPromptModal}
        profile={profile}
        URL={`/${workspaceId}/profile-property?sharedprofileid=${profile?._id}`}
        setOpenModal={setServingPromptModal}
      />
    </>
  );
};

export default AccordionRow;
